import React, { useEffect, useCallback, useRef } from 'react';
import Button from './Button';
import { useSounds } from '../../hooks';

interface GrammarError {
  word: string;
  suggestion: string;
  type: string;
}

interface QuizQuestion {
  question: string;
  options: string[];
  correctIndex: number;
}

interface QuizResult {
  questions: QuizQuestion[];
  userAnswers: number[];
}

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'success' | 'error' | 'info';
  title: string;
  message: string;
  score?: number;
  buttonText?: string;
  onButtonClick?: () => void;
  // For showing what was wrong (Grammar)
  userAnswer?: string;
  correctAnswer?: string;
  errors?: GrammarError[];
  // For quiz results (Reading/Listening)
  quizResult?: QuizResult;
  // Try again option
  onTryAgain?: () => void;
}

// Helper function to normalize text for comparison (remove punctuation)
const normalizeForComparison = (text: string): string => {
  return text.toLowerCase().replace(/[.,!?;:'"()-]/g, '').trim();
};

// Helper function to highlight wrong words
const highlightDifferences = (userText: string, correctText: string) => {
  const userWords = userText.trim().split(/\s+/);
  const correctWords = correctText.trim().split(/\s+/);

  return userWords.map((word, index) => {
    const correctWord = correctWords[index] || '';
    // Compare normalized versions (ignoring punctuation and case)
    const isWrong = normalizeForComparison(word) !== normalizeForComparison(correctWord);

    return {
      word,
      correctWord,
      isWrong,
      isMissing: index >= correctWords.length,
    };
  });
};

const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onClose,
  type,
  title,
  message,
  score,
  buttonText = 'Continue',
  onButtonClick,
  userAnswer,
  correctAnswer,
  errors,
  quizResult,
  onTryAgain,
}) => {
  const { playSound } = useSounds();
  const hasPlayedSoundRef = useRef(false);

  const handleButtonClick = useCallback(() => {
    onButtonClick?.();
    onClose();
  }, [onButtonClick, onClose]);

  // Play sound when modal opens
  useEffect(() => {
    if (isOpen && !hasPlayedSoundRef.current) {
      hasPlayedSoundRef.current = true;
      if (type === 'success') {
        playSound('success');
      } else if (type === 'error') {
        playSound('error');
      }
    }
    if (!isOpen) {
      hasPlayedSoundRef.current = false;
    }
  }, [isOpen, type, playSound]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';

      // Handle keyboard events (Enter or Escape to close)
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === 'Escape') {
          e.preventDefault();
          handleButtonClick();
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => {
        document.body.style.overflow = 'unset';
        window.removeEventListener('keydown', handleKeyDown);
      };
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isOpen, handleButtonClick]);

  if (!isOpen) return null;

  const icons = {
    success: { emoji: '\u2B50', bg: 'bg-kids-success' },
    error: { emoji: '\u{1F914}', bg: 'bg-kids-error' },
    info: { emoji: '\u{1F4A1}', bg: 'bg-kids-primary' },
  };

  const { emoji, bg } = icons[type];
  const whale = '\u{1F433}';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-slate-800 rounded-3xl p-8 max-w-md w-full animate-pop shadow-2xl dark:shadow-slate-900">
        {/* Jumping Whale Mascot */}
        <div className="absolute -top-12 left-1/2 transform -translate-x-1/2">
          <span className="text-6xl animate-jump-once drop-shadow-lg">{whale}</span>
        </div>

        {/* Icon */}
        <div className={`w-20 h-20 ${bg} rounded-full flex items-center justify-center mx-auto mb-4 mt-4`}>
          <span className="text-4xl">{emoji}</span>
        </div>

        {/* Title */}
        <h2 className="text-3xl font-bold text-center text-gray-800 dark:text-white mb-2">
          {title}
        </h2>

        {/* Score */}
        {score !== undefined && (
          <div className="text-center mb-4">
            <span className="text-5xl font-bold text-kids-primary dark:text-indigo-400">{score}%</span>
          </div>
        )}

        {/* Message */}
        <p className="text-center text-gray-600 dark:text-gray-300 text-lg mb-4">
          {message}
        </p>

        {/* Show comparison when there's an error (Grammar) */}
        {type === 'error' && userAnswer && correctAnswer && (
          <div className="bg-gray-50 dark:bg-slate-700 rounded-2xl p-4 mb-6 space-y-3">
            {/* User's answer with wrong words highlighted */}
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">You wrote:</p>
              <p className="font-medium bg-white dark:bg-slate-600 rounded-xl px-3 py-2 flex flex-wrap gap-1">
                {highlightDifferences(userAnswer, correctAnswer).map((item, i) => (
                  <span
                    key={i}
                    className={`${
                      item.isWrong
                        ? 'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400 px-1 rounded border-b-2 border-red-400'
                        : 'text-green-600 dark:text-green-400'
                    }`}
                    title={item.isWrong ? `Should be: ${item.correctWord}` : ''}
                  >
                    {item.word}
                  </span>
                ))}
              </p>
            </div>

            {/* Correct answer */}
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Correct answer:</p>
              <p className="text-green-600 dark:text-green-400 font-medium bg-green-50 dark:bg-green-900/30 rounded-xl px-3 py-2">
                {correctAnswer}
              </p>
            </div>
          </div>
        )}

        {/* Show quiz results when there are wrong answers (Reading/Listening) */}
        {type === 'error' && quizResult && (
          <div className="bg-gray-50 dark:bg-slate-700 rounded-2xl p-4 mb-6 max-h-60 overflow-y-auto">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3 font-medium">Let's review the answers:</p>
            <div className="space-y-4">
              {quizResult.questions.map((q, qIndex) => {
                const userAnswer = quizResult.userAnswers[qIndex];
                const isCorrect = userAnswer === q.correctIndex;

                if (isCorrect) return null; // Only show wrong answers

                return (
                  <div key={qIndex} className="bg-white dark:bg-slate-600 rounded-xl p-3">
                    <p className="font-medium text-gray-800 dark:text-white text-sm mb-2">
                      {qIndex + 1}. {q.question}
                    </p>
                    <div className="space-y-1 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-red-500">{'\u2717'}</span>
                        <span className="text-red-600 dark:text-red-400">
                          Your answer: <span className="font-medium">{q.options[userAnswer]}</span>
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-green-500">{'\u2713'}</span>
                        <span className="text-green-600 dark:text-green-400">
                          Correct: <span className="font-medium">{q.options[q.correctIndex]}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Buttons */}
        <div className="space-y-3">
          {/* Try Again button */}
          {onTryAgain && (
            <Button
              onClick={() => {
                onTryAgain();
                onClose();
              }}
              variant={type === 'success' ? 'secondary' : 'primary'}
              size="large"
              fullWidth
            >
              Try Again
            </Button>
          )}

          {/* Continue/Next button */}
          <Button
            onClick={handleButtonClick}
            variant={onTryAgain ? (type === 'success' ? 'success' : 'secondary') : (type === 'error' ? 'warning' : 'success')}
            size="large"
            fullWidth
          >
            {buttonText}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FeedbackModal;
